import React, { useEffect, useState, useCallback } from "react"
import get from "lodash/get"
import { graphql } from "gatsby"
import ChevronUp from "../svg/chevron-up.svg"

import Grid from "../components/grid/grid.component"

import { CheatsheetContainer, BackToTop } from "./cheat-sheets.styles"
import CheatSheetLayout from "../layouts/cheat-sheet/cheat-sheet.layout"
import { Seo } from "../components/seo"
import CheatsheetCTA from "../components/cheatsheet-CTA/cheatsheet-CTA.component"

// this page use custom meta data
export const Head = ({ location, data }) => {
  const cheatsheet = data.contentfulCheatsheet
  // fallback values are cheatsheet name and excerpt
  const customMetaData = {
    pageTitle: cheatsheet.seoPageTitle || cheatsheet.name,
    pageDescription: cheatsheet.seoPageDesc || cheatsheet.excerpt,
    pageRobots: "index,follow",
  }
  return (
    <Seo
      metaData={customMetaData}
      uri={location.pathname}
      embedOverride={`https:${cheatsheet.thumbnail.file.url}`}
      headScripts={cheatsheet.jsonLdScripts?.childMarkdownRemark.html}
    />
  )
}

const CheatSheetTemplate = (props) => {
  const cheatsheet = get(props, "data.contentfulCheatsheet")

  // Mailerlite group map, located in General Setting Model on contentful
  const mailerliteGroupIdMap = JSON.parse(
    get(
      props,
      "data.contentfulGeneralSettingsMailerliteGroupIdMapJsonNode.internal.content"
    )
  )

  const [ScrollStatus, setScrollStatus] = useState(false)
  const memoizedScrollHandler = useCallback(() => {
    // show the "back to top" button when the user scrolls pass the table of contents
    const contentPos = document.getElementById("cheatsheet-content").offsetTop
    if (window.scrollY > contentPos) {
      setScrollStatus(true)
    } else {
      setScrollStatus(false)
    }
  }, [])
  useEffect(() => {
    window.addEventListener("scroll", memoizedScrollHandler)
    return () => window.removeEventListener("scroll", memoizedScrollHandler)
  },
  // eslint-disable-next-line
  [props.path, mailerliteGroupIdMap])

  const shortIntro = cheatsheet.shortIntro.childMarkdownRemark.html
  const intro = cheatsheet.intro.childMarkdownRemark.html
  const toc = cheatsheet.contents.childMarkdownRemark.html
  const part1 = cheatsheet.pageOne.childMarkdownRemark.html
  const part2 = cheatsheet.pageTwo
    ? cheatsheet.pageTwo.childMarkdownRemark.html
    : ""
  const part3 = cheatsheet.pageThree
    ? cheatsheet.pageThree.childMarkdownRemark.html
    : ""

  const html = part1 + part2 + part3

  const memoizedTopFunction = useCallback(() => {
    // so that we can scroll back to the top of the table of contents
    const tocPos = document.getElementById("cheatsheet-toc").offsetTop
    window.scrollTo({ top: tocPos - 80, behavior: "smooth" })
  }, [])

  return (
    <CheatSheetLayout mt={[60, 60, 80, 80]} mb={[50, 50, 90, 90]}>
      <Grid>
        <CheatsheetContainer
          className="intro-container" // then we can remove the p tag with class in Contentful Markdown field
          dangerouslySetInnerHTML={{
            __html: shortIntro,
          }}
        />
        <CheatsheetCTA
          slug={cheatsheet.slug}
          group={mailerliteGroupIdMap[cheatsheet.mlOption]}
          name={cheatsheet.name}
        />
        <CheatsheetContainer
          className="intro-container" // then we can remove the p tag with class in Contentful Markdown field
          dangerouslySetInnerHTML={{
            __html: intro,
          }}
        />

        <CheatsheetContainer
          id="cheatsheet-toc"
          className="toc-container"
          dangerouslySetInnerHTML={{
            __html: toc,
          }}
        />
        <CheatsheetContainer
          id="cheatsheet-content"
          dangerouslySetInnerHTML={{
            __html: html,
          }}
        />
      </Grid>

      <BackToTop pageScrolled={ScrollStatus} onClick={memoizedTopFunction}>
        <ChevronUp />
      </BackToTop>
    </CheatSheetLayout>
  )
}

export default CheatSheetTemplate

export const pageQuery = graphql`
  query SheetBySlug($sheetSlug: String!) {
    contentfulGeneralSettingsMailerliteGroupIdMapJsonNode {
      internal {
        content
      }
    }
    contentfulCheatsheet(slug: { eq: $sheetSlug }) {
      jsonLdScripts {
        childMarkdownRemark {
          html
        }
      }
      seoPageTitle
      seoPageDesc
      mlOption
      shortIntro {
        childMarkdownRemark {
          html
        }
      }
      intro {
        childMarkdownRemark {
          html
        }
      }
      contents {
        childMarkdownRemark {
          html
        }
      }
      thumbnail {
        file {
          url
        }
      }
      slug
      name
      excerpt
      pageOne {
        childMarkdownRemark {
          html
        }
      }
      pageTwo {
        childMarkdownRemark {
          html
        }
      }
      pageThree {
        childMarkdownRemark {
          html
        }
      }
    }
  }
`
